import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { ExtraFilter, FilterComponent, FilterLayout } from "../Filters/FilterTypes";

export enum SortDirections {
  ASC = "ASCENDING",
  DESC = "DESCENDING",
}

export enum SelectionMode {
  none = 0,
  single = 1,
  multiple = 2,
}

export enum EmptyListingButtonTypes {
  PRIMARY = "PRIMARY",
  DEFAULT = "DEFAULT",
}

export interface ListingColumn {
  /**
   * Unique key that identifies the column
   */
  itemProp: string;
  /**
   * id used to get the ListingColumn header element
   */
  testId?: string;
  /**
   * Column display name
   */
  name: string;
  /**
   * The width of the column is a portion of the available space equal to this value divided by the sum
   * of all proportional column widths in the list.
   */
  flexGrow?: number;
  /**
   * Specifies if the column can be resized
   */
  isResizable?: boolean
  /**
   * Function to render a custom component
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onRenderItems?: (value: any) => JSX.Element;
  /**
   * Initial sort direction of the column
   */
  initialSortDirection?: SortDirections;
  /**
   * Custom comparator to use when sorting
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  comparator?: (a: any, b: any) => 0 | -1 | 1
}

export interface ListingSelectOption {
  /**
   *  Key of the select option
   */
  key: string | number;
  /**
   *  Display name of the option
   */
  text: string;
}

export interface ViewSelectOption extends ListingSelectOption {
  /**
   * Add an icon to the option display name
   */
  icon?: string;
}

export interface GroupingSelect {
  /**
   * List of grouping options
   */
  groupingOptions: ListingSelectOption[],
  /**
   * Default selected grouping option
   */
  defaultSelectedKey?: string,
  /**
   * Handler that triggers when the selected grouping option changes
   */
  onGroupingChange?: (event: React.FormEvent<HTMLDivElement>, option?: ListingSelectOption, index?: number) => void
}

export interface ViewSelect {
  /**
   * List of view options
   */
  viewOptions: ListingSelectOption[],
  /**
   * Default selected view option
   */
  defaultSelectedKey?: string,
  /**
   * Handler that triggers when the selected view option changes
   */
  onViewChange?: (event: React.FormEvent<HTMLDivElement>, option?: ListingSelectOption, index?: number) => void;
}

export interface ObjectWithKey {
  /**
   * Object key
   */
  key?: string | number;
}

export interface EmptyButtonMenu {
  /**
   * Key of the menu button
   */
  key: string;
  /**
   * Text to display on the menu button
   */
  text: string;
  /**
   * id used to get the EmptyButtonMenu
   */
  testId?: string;
  /**
   * A handler that triggers once the button is clicked
   */
  onClick: () => void;
}

export interface EmptyButtonMenuItems {
  /**
   * List of menu items under a menu button
   */
  items: EmptyButtonMenu [];
}

export interface EmptyButton {
  /**
   * Specifies if the button is primary or default
   */
  type: EmptyListingButtonTypes;
  /**
   * Text to display on the button
   */
  text: string;
  /**
   * Action to perform when the button is clicked
   */
  action?: () => void;
  /**
   * Specifies if the button is a menu button
   */
  menu?: EmptyButtonMenuItems;
}

export interface ActionMenuItem {
  /**
   * A key that uniquely identifies the action
   */
  key: string;
  /**
   * Name of the action
   */
  name: string;
  /**
   * An icon to display in the action
   */
  icon: string;
  /**
   * Operation to perform when clicking on the action
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (value: any) => void;
  /**
   * id used to get the ActionMenuItem
   */
  testId?: string;
}

export interface SortOptions {
  /**
   * The locale used for sorting
   */
  locale: string;
  /**
   * Initially sorted column
   */
  initialSortedColumn?: string;
}

export interface FilterOptions {
  /**
   * List of all filters supported in the list
   */
  allExtraFilters: ExtraFilter[];
  /**
   * List of default filters to show in the list by default
   */
  defaultExtraFilters?: ExtraFilter[];
  /**
   * Hide text input select filter
   */
  hideTextFilter?: boolean;
  /**
   * Hide add filter button
   */
  hideAddFilterPill?: boolean;
  /**
   * Layout type for the Filter
   */
  filterLayout?: FilterLayout;
  /**
   * Text filter callback function
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  textFilterCallBack?: (item: any) => any;
  /**
   * Component reference for the FilterComponent
   */
  componentRef?: (ref: FilterComponent) => void;
  /**
   * Default filter text value
   */
  defaultFilterText?: string;
  /**
   * Clear filter callback function
   */
  onClearFilters?: () => void;
}

export interface ListingTestIds extends ComponentTestIds{
  /**
   * The loading dots that are displayed when the listing is loading
   */
  loadingDots: string,
  /**
   * A row inside the listing.
   * To access a particular row use its corresponding index in the array of rows returned by getAllByTestId
   */
  row: string,
  /**
   * The checkbox to select a row
   * To access a particular checkbox use its corresponding index in the array of rows returned by getAllByTestId
   */
  rowCheckbox: string,
  /**
   * The action menu button displayed on the far right of the row
   */
  rowActionMenuButton: string,
  /**
   * The watermark that is displayed when the listing has no items to show
   */
  emptyListingWatermark: string,
  /**
   * The clear filter button in the watermark
   */
  emptyListingWatermarkClearFilterButton: string,
  /**
   * The primary button in the watermark
   */
  emptyListingWatermarkPrimaryButton: string,
}

export const buildListingTestIds = (baseTestId?: string): ListingTestIds => ({
  component: buildTestId(baseTestId),
  loadingDots: buildTestId(baseTestId, "-loading-dots"),
  row: buildTestId(baseTestId, "-row"),
  rowCheckbox: buildTestId(baseTestId, "-row-checkbox"),
  rowActionMenuButton: buildTestId(baseTestId, "-row-action-menu-button"),
  emptyListingWatermark: buildTestId(baseTestId, "-watermark"),
  emptyListingWatermarkClearFilterButton: buildTestId(baseTestId, "-watermark-clear-filters-button"),
  emptyListingWatermarkPrimaryButton: buildTestId(baseTestId, "-watermark-primary-button"),
});
